// Local tempo: a tempogram over a sliding window, and the best period path through it.
//
// A live band speeds up and slows down, so one period for the whole song is wrong
// exactly where the dancer needs it. Here the onset envelope is cut into windows of
// about 8 s every 2 s. In each window, each candidate period (log-spaced within ±15% of
// the tapped tempo, so the octave cannot change) is scored by the
// autocorrelation of the window at that lag plus its multiples (2x, 4x), which
// makes the peak sharp. A Viterbi pass then picks one period per window: the score
// of each candidate is its normalised salience, a change of period between windows
// costs the square of the change in log period (about 1% per hop is cheap, 5% is
// expensive), and windows that hold the seed taps are pulled toward the period the
// taps imply.

export interface TempoCurve {
  /** Times in seconds, ascending. */
  readonly times: Float64Array;
  /** Seconds per beat at each time. */
  readonly periods: Float64Array;
}

/** Period at time t: log-linear interpolation between points, constant outside them. */
export function periodAt(curve: TempoCurve, t: number): number {
  const { times, periods } = curve;
  const n = times.length;
  if (t <= times[0]!) return periods[0]!;
  if (t >= times[n - 1]!) return periods[n - 1]!;
  let lo = 0, hi = n - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (times[mid]! <= t) lo = mid; else hi = mid;
  }
  const u = (t - times[lo]!) / (times[hi]! - times[lo]!);
  return Math.exp(Math.log(periods[lo]!) * (1 - u) + Math.log(periods[hi]!) * u);
}

export function constantCurve(period: number): TempoCurve {
  return { times: Float64Array.of(0, 1), periods: Float64Array.of(period, period) };
}

/** Half-width of the tempo window around the tapped tempo, as a fraction (ARCHITECTURE.md §11: ±8% pinned the curve on a song that goes from 172 to 190 BPM). */
export const TEMPO_WINDOW = 0.15;

export interface TempogramOptions {
  /** Window length in seconds. Default 8. */
  readonly windowSec?: number;
  /** Seconds between windows. Default 2. */
  readonly hopSec?: number;
  /** Candidate periods run from centre*(1-spread) to centre*(1+spread). Default TEMPO_WINDOW (0.15): the window is wide so a song can speed up a long way; the path cost, not the window, limits how fast the tempo moves. */
  readonly spread?: number;
  /** Number of candidate periods, log-spaced. Default 90 (steps of about 0.3%). */
  readonly candidates?: number;
  /** Autocorrelation is taken at these multiples of each candidate lag. Default 1, 2, 4. */
  readonly multiples?: readonly number[];
}

export interface Tempogram {
  /** Window centres, seconds. */
  readonly times: Float64Array;
  /** Window starts and ends, seconds (a window holds taps when both taps lie inside). */
  readonly spans: readonly (readonly [number, number])[];
  /** Candidate periods in seconds, ascending, log-spaced. */
  readonly periods: Float64Array;
  /** salience[w][k]: mean over the multiples of the window's normalised autocorrelation at that lag. 1 is a perfectly periodic window; 0 is none. */
  readonly salience: readonly Float64Array[];
}

/** Tempo salience of each candidate period in each window of the envelope. */
export function tempogram(env: Float64Array, fps: number, centrePeriod: number, options: TempogramOptions = {}): Tempogram {
  const { windowSec = 8, hopSec = 2, spread = TEMPO_WINDOW, candidates = 90, multiples = [1, 2, 4] } = options;
  if (!(centrePeriod > 0) || !(spread > 0 && spread < 1) || candidates < 2) throw new RangeError('bad tempogram parameters');
  const n = env.length;
  const wN = Math.min(n, Math.max(8, Math.round(windowSec * fps)));
  const hop = Math.max(1, Math.round(hopSec * fps));

  const periods = new Float64Array(candidates);
  const lo = Math.log(1 - spread), hi = Math.log(1 + spread);
  for (let k = 0; k < candidates; k++) periods[k] = centrePeriod * Math.exp(lo + ((hi - lo) * k) / (candidates - 1));

  const starts: number[] = [];
  for (let a = 0; a + wN <= n; a += hop) starts.push(a);
  if (starts.length === 0 || starts[starts.length - 1]! + wN < n - hop / 2) starts.push(Math.max(0, n - wN));

  const times = new Float64Array(starts.length);
  const spans: [number, number][] = [];
  const salience: Float64Array[] = [];
  const x = new Float64Array(wN);
  starts.forEach((a, w) => {
    times[w] = (a + wN / 2) / fps;
    spans.push([a / fps, (a + wN) / fps]);
    let mean = 0;
    for (let i = 0; i < wN; i++) mean += env[a + i]!;
    mean /= wN;
    let energy = 0;
    for (let i = 0; i < wN; i++) { x[i] = env[a + i]! - mean; energy += x[i]! * x[i]!; }
    const s = new Float64Array(candidates);
    if (energy > 1e-12) {
      for (let k = 0; k < candidates; k++) {
        const lag = periods[k]! * fps;
        let sum = 0, used = 0;
        for (const m of multiples) {
          const L = m * lag;
          if (L > wN / 2) continue; // too few products left to mean anything
          sum += autocorr(x, wN, L, energy);
          used++;
        }
        s[k] = used ? sum / used : 0;
      }
    }
    salience.push(s);
  });
  return { times, spans, periods, salience };
}

/** Normalised autocorrelation of x (mean removed, energy given) at a fractional lag, by linear interpolation. 1 for a perfectly periodic signal. */
function autocorr(x: Float64Array, n: number, lag: number, energy: number): number {
  let sum = 0, count = 0;
  for (let i = 0; i + lag < n - 1; i++) {
    const p = i + lag;
    const j = Math.floor(p);
    const f = p - j;
    sum += x[i]! * (x[j]! * (1 - f) + x[j + 1]! * f);
    count++;
  }
  return count ? sum / count / (energy / n) : 0;
}

export interface PathOptions {
  /** Taps (seconds on the tempogram's clock) and the period they imply: windows holding at least two taps are pulled toward it. */
  readonly prior?: { readonly taps: readonly number[]; readonly period: number };
  /** Width of the prior as a fraction of the period (one standard deviation). Default 0.03. */
  readonly priorWidth?: number;
  /** Change of period per hop that costs one unit, as a fraction (the cost is quadratic). Default 0.02: about 1% per hop is cheap, 5% expensive. */
  readonly stepScale?: number;
  /** Weight of a window's salience against the costs. Default 4. */
  readonly emissionWeight?: number;
}

/**
 * The period path through a tempogram that maximises
 *   sum over windows of  w * emission  -  prior  -  sum over hops of 0.5 * (change in log period / stepScale)^2
 * by Viterbi. The emission of a candidate is its salience above the window's least salient candidate, relative to
 * a typical window (the 90th percentile of the windows' ranges, so a window with no periodicity, such as a silent break,
 * has emission near 0 everywhere and the path simply continues through it).
 */
export function periodPath(tg: Tempogram, options: PathOptions = {}): TempoCurve {
  const { priorWidth = 0.03, stepScale = 0.02, emissionWeight = 4, prior } = options;
  const W = tg.times.length;
  const K = tg.periods.length;
  const logP = Array.from(tg.periods, Math.log);

  const ranges = tg.salience.map((s) => Math.max(...s) - Math.min(...s));
  const ref = Math.max(1e-9, [...ranges].sort((a, b) => a - b)[Math.floor(0.9 * (W - 1))]!);

  const cost: Float64Array[] = []; // cost[w][k] = -(emission) + prior, lower is better
  for (let w = 0; w < W; w++) {
    const s = tg.salience[w]!;
    const floor = Math.min(...s);
    const [a, b] = tg.spans[w]!;
    const inside = prior ? prior.taps.filter((t) => t >= a && t <= b).length : 0;
    const c = new Float64Array(K);
    for (let k = 0; k < K; k++) {
      c[k] = -emissionWeight * Math.min(1.5, (s[k]! - floor) / ref);
      if (prior && inside >= 2) {
        const z = (logP[k]! - Math.log(prior.period)) / priorWidth;
        c[k]! += 0.5 * z * z;
      }
    }
    cost.push(c);
  }

  // Viterbi. The transition cost depends only on the two candidates' log periods.
  const trans = new Float64Array(K * K);
  for (let i = 0; i < K; i++) for (let j = 0; j < K; j++) {
    const d = (logP[i]! - logP[j]!) / stepScale;
    trans[i * K + j] = 0.5 * d * d;
  }
  const best = new Float64Array(K);
  const back: Int32Array[] = [];
  for (let k = 0; k < K; k++) best[k] = cost[0]![k]!;
  for (let w = 1; w < W; w++) {
    const next = new Float64Array(K);
    const from = new Int32Array(K);
    for (let k = 0; k < K; k++) {
      let m = Infinity, arg = 0;
      for (let j = 0; j < K; j++) {
        const v = best[j]! + trans[k * K + j]!;
        if (v < m) { m = v; arg = j; }
      }
      next[k] = m + cost[w]![k]!;
      from[k] = arg;
    }
    best.set(next);
    back.push(from);
  }
  let k = 0;
  for (let j = 1; j < K; j++) if (best[j]! < best[k]!) k = j;
  const path = new Float64Array(W);
  for (let w = W - 1; w >= 0; w--) {
    path[w] = tg.periods[k]!;
    if (w > 0) k = back[w - 1]![k]!;
  }
  return { times: tg.times, periods: path };
}

/** Tempogram plus Viterbi path: the local period curve of an envelope. */
export function tempoCurve(env: Float64Array, fps: number, centrePeriod: number, options: TempogramOptions & PathOptions = {}): TempoCurve {
  return periodPath(tempogram(env, fps, centrePeriod, options), options);
}

/**
 * The local period read off a tracked beat list: at each beat, the least-squares slope of beat time against
 * beat number over the beats within ±halfWindow seconds (at least 5 beats). Unlike the tempogram it has no
 * range limit and no interpolation bias, but it is only as right as the beats.
 */
export function curveFromBeats(beats: readonly number[], halfWindow = 4): TempoCurve {
  const n = beats.length;
  if (n < 2) throw new RangeError('curveFromBeats needs at least two beats');
  const times = new Float64Array(n);
  const periods = new Float64Array(n);
  let a = 0, b = 0;
  for (let i = 0; i < n; i++) {
    while (beats[a]! < beats[i]! - halfWindow) a++;
    while (b + 1 < n && beats[b + 1]! <= beats[i]! + halfWindow) b++;
    let lo = a, hi = b;
    while (hi - lo + 1 < 5 && (lo > 0 || hi < n - 1)) {
      if (lo > 0) lo--;
      if (hi - lo + 1 < 5 && hi < n - 1) hi++;
    }
    const m = hi - lo + 1;
    let sx = 0, sy = 0, sxx = 0, sxy = 0;
    for (let j = lo; j <= hi; j++) { sx += j; sy += beats[j]!; sxx += j * j; sxy += j * beats[j]!; }
    const slope = m >= 2 ? (m * sxy - sx * sy) / (m * sxx - sx * sx) : beats[1]! - beats[0]!;
    times[i] = beats[i]!;
    periods[i] = slope;
  }
  return { times, periods };
}
